/**
 * The event taxonomy, named once.
 *
 * WHY THIS FILE EXISTS. Before it, the analytics module was complete and correct and there was
 * not one `trackEvent` call anywhere in either product. Both surfaces sent pageviews and nothing
 * else, which answers *how many* and can never answer *which tool converts* — the question the
 * whole lead taxonomy was built for.
 *
 * Pageviews are free; events are a decision about what you want to be able to ask later. So the
 * names are here, as constants, rather than as string literals at call sites. A funnel built from
 * `"Clicked Button 3"` is a funnel nobody can read six weeks later, and a typo'd event name is a
 * funnel with a silent hole in it.
 *
 * THE CONVENTION, kept from analytics.ts: `noun_verb`, past tense. `product` is stamped
 * automatically on every event, so no name needs to carry it.
 *
 * These names are mirrored in each product's `standard.config.json` under
 * `analytics.required_events`, and `make verify-standard` fails if one of them is never
 * emitted — so an event can be added here and forgotten, but not declared and forgotten.
 *
 * ## THIS FILE IS THE KIT. Do not edit the copy in a product — see web-kit/README.md.
 */

import { trackEvent } from "./analytics";

export const EVENTS = {
  /** A visitor began using a free tool — pasted a URL, opened the verifier, started a scan. */
  TOOL_STARTED: "tool_started",
  /** …and got a result. The pair is the activation rate; either alone is half a story. */
  TOOL_COMPLETED: "tool_completed",
  /** Any call to action, anywhere. `placement` is what makes it useful. */
  CTA_CLICKED: "cta_clicked",
  /** An email ask was shown. Denominator for the two below. */
  LEAD_MODAL_OPENED: "lead_modal_opened",
  LEAD_SUBMITTED: "lead_submitted",
  /** Recorded on purpose. A form that fails silently is how you lose the leads you counted. */
  LEAD_FAILED: "lead_failed",
  /** Self-serve signup returned a key. The end of the funnel this product actually has. */
  KEY_CREATED: "key_created",
} as const;

type Props = Record<string, string | number | boolean | undefined>;

/** A visitor started a tool. `tool` is the id from TOOLS, not the page title. */
export function trackToolStarted(tool: string, props?: Props): void {
  trackEvent(EVENTS.TOOL_STARTED, { tool, ...props });
}

/**
 * A tool produced a result.
 *
 * `outcome` is the shape of the answer, never the answer itself — "graded" rather than the host
 * that was graded, "verified" rather than the signature. The subject of a scan is the visitor's
 * business and putting it in an analytics vendor is a different promise from the one /privacy
 * makes.
 */
export function trackToolCompleted(
  tool: string,
  outcome: string,
  props?: Props & { duration_ms?: number },
): void {
  trackEvent(EVENTS.TOOL_COMPLETED, { tool, outcome, ...props });
}

/**
 * Somebody clicked a call to action.
 *
 * `placement` is the field that earns this event's keep: the same "Get a key" appears in the
 * nav, the hero, the pricing table and the footer, and knowing which one converts is the
 * difference between moving a button and guessing.
 */
export function trackCta(cta: string, placement: string, destination?: string): void {
  trackEvent(EVENTS.CTA_CLICKED, { cta_id: cta, placement, destination });
}

/** An email ask became visible. Fired where it is rendered, not where it is defined. */
export function trackLeadShown(source: string, trigger: string, subject?: string): void {
  trackEvent(EVENTS.LEAD_MODAL_OPENED, { source, trigger, subject });
}

export function trackLeadSubmitted(source: string, props?: Props): void {
  trackEvent(EVENTS.LEAD_SUBMITTED, { source, ...props });
}

/** `reason` is a code or a status, never the message — messages change, funnels should not. */
export function trackLeadFailed(source: string, reason: string): void {
  trackEvent(EVENTS.LEAD_FAILED, { source, reason });
}

/** A key was issued to somebody with no account. `from_surface` says what persuaded them. */
export function trackKeyCreated(fromSurface: string, fromRoute?: string): void {
  trackEvent(EVENTS.KEY_CREATED, { from_surface: fromSurface, from_route: fromRoute });
}
